import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

const PasswordSetupPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const token = searchParams.get('token');
  const isInitialSetup = searchParams.get('setup') === 'true';

  useEffect(() => {
    if (!token) {
      setError('No setup token provided. Please check your email link.');
    }
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError('No setup token provided.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await api.post('/auth/reset-password', {
        token: token,
        newPassword: formData.password
      });

      if (response.data.success) {
        setSuccess(true);
        
        // Simple URL-based redirect detection
        setTimeout(() => {
          const isProduction = window.location.hostname.includes('.memoraize.io');
          if (isProduction) {
            // Already on subdomain - stay here for branded experience
            navigate('/login');
          } else {
            // On localhost/development - stay local
            navigate('/login');
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Password setup error:', err);
      setError(err.response?.data?.message || 'Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <h1 style={{
            color: '#4a90e2',
            marginBottom: '0.5rem'
          }}>
            memorAIze
          </h1>
          <p style={{
            color: '#666',
            margin: 0,
            fontSize: '14px'
          }}>
            Modern Quran Education Platform
          </p>
        </div>

        {/* Form */}
        <div>
          <h2 style={{
            textAlign: 'center',
            color: '#333',
            marginBottom: '1.5rem'
          }}>
            {isInitialSetup ? 'Set Your Password' : 'Create New Password'}
          </h2>
          
          {error && (
            <div style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              Password set successfully! Redirecting to login...
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontWeight: '500',
                color: '#333'
              }}>
                New Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your new password"
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontWeight: '500',
                color: '#333'
              }}>
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your new password"
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !token}
              style={{
                width: '100%',
                backgroundColor: loading || !token ? '#90caf9' : '#4a90e2',
                color: 'white',
                border: 'none',
                padding: '12px',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: loading || !token ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Setting Password...' : 'Set Password'}
            </button>
          </form>

          <div style={{
            textAlign: 'center',
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px'
          }}>
            <p style={{
              margin: 0,
              fontSize: '12px',
              color: '#666'
            }}>
              Password Requirements:
            </p>
            <ul style={{
              margin: '0.5rem 0 0 0',
              paddingLeft: '1rem',
              fontSize: '12px',
              color: '#666',
              textAlign: 'left'
            }}>
              <li>At least 8 characters long</li>
              <li>Use a strong, unique password</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordSetupPage;